import type { BoardQueueOutput, BoardStation } from './board-types'

const BOARD_QUEUE_STREAM_PATH = '/api/public/queue/stream'
const BRANCH_NOT_FOUND_STATUS = 404

type BoardQueueStreamSnapshot = BoardQueueOutput | BoardStation[] | null | undefined

export const getBoardQueueStreamUrl = (branchId: number, apiUrl?: string) => {
    const path = `${BOARD_QUEUE_STREAM_PATH}?${new URLSearchParams({ branch_id: String(branchId) })}`

    if (!apiUrl) {
        return path
    }

    return new URL(path, apiUrl).toString()
}

export const createBoardQueueEventSource = (
    branchId: number,
    apiUrl = import.meta.env?.VITE_API_URL as string | undefined
) => {
    return new EventSource(getBoardQueueStreamUrl(branchId, apiUrl), { withCredentials: false })
}

/**
 * EventSource не отдает HTTP-статус, поэтому доступность филиала проверяем отдельным запросом
 * к тому же стриму: читаем только заголовки ответа и сразу обрываем соединение.
 */
export const isBoardBranchNotFound = async (
    branchId: number,
    apiUrl = import.meta.env?.VITE_API_URL as string | undefined
) => {
    const controller = new AbortController()

    try {
        const response = await fetch(getBoardQueueStreamUrl(branchId, apiUrl), {
            headers: { Accept: 'text/event-stream' },
            signal: controller.signal,
        })

        return response.status === BRANCH_NOT_FOUND_STATUS
    } finally {
        controller.abort()
    }
}

const normalizeStationQueue = (station: BoardStation): BoardStation => {
    return {
        ...station,
        queue: station.queue ?? [],
    }
}

export const normalizeBoardQueueStreamSnapshot = (
    snapshot: BoardQueueStreamSnapshot
): BoardQueueOutput => {
    const stations = Array.isArray(snapshot)
        ? snapshot
        : snapshot && typeof snapshot === 'object' && Array.isArray(snapshot.stations)
          ? snapshot.stations
          : []

    return {
        ...(!Array.isArray(snapshot) && snapshot && typeof snapshot === 'object' ? snapshot : {}),
        stations: stations.map((station) => normalizeStationQueue(station)),
    }
}

export const parseBoardQueueStreamSnapshot = (data: string): BoardQueueOutput => {
    const value = data.trim()

    if (!value || value === 'undefined' || value === 'null') {
        return normalizeBoardQueueStreamSnapshot(undefined)
    }

    return normalizeBoardQueueStreamSnapshot(JSON.parse(value) as BoardQueueStreamSnapshot)
}
